import React, { useState, useEffect } from "react"
import DocumentList from "../components/documentList"
import PdfView from "../components/pdfView"
import JsonView from "../components/jsonView"
import { get, post, put } from "./../utils/apiClient"
import { MISSED_FIELDS } from "./../utils/constant"
import "../css/style.css"
const interval = 55 * 60 * 1000 // 55 minutes in milliseconds

const Dashboard = ({ userId }) => {
  const [zipFiles, setZipFiles] = useState({})
  const [pdfUrl, setPdfUrl] = useState(null)
  const [jsonContent, setJsonContent] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hoveredKey, setHoveredKey] = useState(null) // State to track hovered key
  const [colorIndexes, setColorIndexes] = useState({}) // State to track selected category
  const [hoveredPage, setHoveredPage] = useState(null) // State to track hovered page
  const [loading, setLoading] = useState(true)
  const [listLoading, setListLoading] = useState(false)
  const [fileLoading, setFileLoading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [isFileLocked, setIsFileLocked] = useState(false)
  const [isValidUser, setIsValidUser] = useState(false)
  const [intervalId, setIntervalId] = useState(null)

  const handleFileClick = async (groupName, fileName) => {
    try {
      setFileLoading(true)

      const data = await post("/clarity/download", {
        groupName,
        fileName,
        userId,
      })

      // Convert the base64 PDF content to a Blob
      if (data?.pdfContent) {
        const byteCharacters = atob(data?.pdfContent)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: "application/pdf" })

        // Update state to pass the Blob to the Document component
        setPdfUrl(blob)
      }

      if (data?.jsonContent) {
        const decodedJsonContent = atob(data?.jsonContent)
        const jsonObjectTmp = JSON.parse(decodedJsonContent)
        const jsonObject = addMissingFields(jsonObjectTmp)
        // console.log("->  jsonObject:", jsonObjectTmp)
        // console.log("->  jsonObjectWithMissingFields:", jsonObject)

        setJsonContent(jsonObject)
      }

      const isLocked = !data?.pdfContent ? true : false

      startApiCalls(groupName, fileName)

      setIsFileLocked(isLocked)
      setFileLoading(false)
      setCurrentPage(1)
      setSelectedGroup(groupName)
    } catch (error) {
      setFileLoading(false)
      setJsonContent(null)
      console.error("Error in handleFileClick:", error)
    }
  }

  const addMissingFields = (json) => {
    const newJson = structuredClone(json)

    Object.keys(MISSED_FIELDS).forEach((category) => {
      if (!newJson["result"][category]) return // Skip if category is missing

      const fieldsToAdd = MISSED_FIELDS[category]

      if (Array.isArray(newJson["result"][category])) {
        // Iterate over each object in the array
        newJson["result"][category].forEach((entry, index) => {
          fieldsToAdd.forEach((field) => {
            if (!(field in entry) && newJson["result"][category][index]) {
              newJson["result"][category][index][field] =
                field === "Quantity" ? { data: 1 } : { data: "" } // Add missing field // specific fix for "Quantity"
            }
          })
        })
      } else if (
        typeof newJson["result"][category] === "object" &&
        newJson["result"][category] !== null
      ) {
        // If the category is a single object, add missing fields
        fieldsToAdd.forEach((field) => {
          if (!(field in newJson["result"][category])) {
            newJson["result"][category][field] = { data: "" } // Add missing field
          }
        })
      }
    })
    return newJson
  }

  // Set interval function
  const startApiCalls = (groupName, fileName) => {
    // Ensure only one interval runs at a time
    if (intervalId) clearInterval(intervalId)

    // Set up an interval to call the API every hour
    const newIntervalId = setInterval(async () => {
      // const timeNow = new Date()
      // console.log(`setInterval at: ${timeNow}`)
      try {
        await put(`/clarity/increaseLockHour?userId=${userId}`)
      } catch (err) {
        console.log("Error in setInterval:", err)
      }
    }, interval)

    setIntervalId(newIntervalId)
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
  }

  const fetchFilesData = async () => {
    try {
      const data = await get(`/clarity/files?userId=${userId}`)

      setZipFiles(data.files)

      if (Object.keys(data.files).length > 0) setIsValidUser(true)
    } catch (err) {
      setLoading(false)
    }
  }

  const reloadDashboard = async () => {
    setLoading(true)
    await fetchFilesData()
    setLoading(false)
  }

  const reloadFiles = async () => {
    setListLoading(true)
    await fetchFilesData()
    setListLoading(false)
    setPdfUrl(null)
    setJsonContent(null)
    setSelectedFile(null)
  }

  useEffect(() => {
    reloadDashboard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cleanup interval on component unmount
  React.useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [intervalId])

  useEffect(() => {
    if (hoveredPage !== currentPage) {
      setCurrentPage(hoveredPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredPage])

  useEffect(() => {
    if (currentPage) setHoveredPage(currentPage)
  }, [currentPage])

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-message">Loading...</div>
      </div>
    )
  }

  return (
    <div className="container">
      <DocumentList
        zipFiles={zipFiles}
        onFileClick={handleFileClick}
        selectedFile={selectedFile}
        setSelectedFile={(file) => setSelectedFile(file)}
        fetchFilesData={() => reloadFiles()}
        listLoading={listLoading}
        isValidUser={isValidUser}
      />

      <PdfView
        pdfUrl={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        numPages={numPages}
        jsonContent={jsonContent}
        setHoveredKey={setHoveredKey}
        hoveredKey={hoveredKey}
        colorIndexes={colorIndexes}
        fileLoading={fileLoading}
        isFileLocked={isFileLocked}
        isValidUser={isValidUser}
      />

      <JsonView
        jsonContent={jsonContent}
        setHoveredKey={setHoveredKey}
        hoveredKey={hoveredKey}
        setHoveredPage={setHoveredPage}
        fileLoading={fileLoading}
        colorIndexes={colorIndexes}
        selectedFile={selectedFile}
        selectedGroup={selectedGroup}
        isFileLocked={isFileLocked}
        userId={userId}
        setColorMapping={(value) => {
          setColorIndexes(value)
        }}
        resetSelectedFileContent={() => {
          setPdfUrl(null)
          setJsonContent(null)
          setSelectedFile(null)
          fetchFilesData()
        }}
      />
    </div>
  )
}

export default Dashboard
